import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "../services/api";
import {
    FaLock,
    FaEye,
    FaEyeSlash,
    FaCheckCircle,
    FaExclamationTriangle,
    FaSpinner,
    FaArrowLeft
} from "react-icons/fa";

function ResetPassword() {
    const { token } = useParams();
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        password: "",
        confirmPassword: ""
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState("");
    const [tokenValid, setTokenValid] = useState(true);

    useEffect(() => {
        // Validate token on component mount
        if (!token) {
            setTokenValid(false);
            setError("Invalid or missing reset token.");
        }
    }, [token]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        // Clear error when user starts typing
        if (error) setError("");
    };

    const validatePassword = (password) => {
        const minLength = 6;
        const hasUpperCase = /[A-Z]/.test(password);
        const hasLowerCase = /[a-z]/.test(password);
        const hasNumbers = /\d/.test(password);

        return {
            length: password.length >= minLength,
            upperCase: hasUpperCase,
            lowerCase: hasLowerCase,
            numbers: hasNumbers,
            isValid: password.length >= minLength && hasUpperCase && hasLowerCase && hasNumbers
        };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");

        // Validate passwords
        if (formData.password !== formData.confirmPassword) {
            setError("Passwords do not match.");
            return;
        }

        const passwordValidation = validatePassword(formData.password);
        if (!passwordValidation.isValid) {
            setError("Password must be at least 6 characters long and contain uppercase, lowercase, and numbers.");
            return;
        }

        setLoading(true);

        try {
            await api.post("/auth/reset-password", {
                token,
                password: formData.password
            });
            setSuccess(true);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to reset password. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    if (!tokenValid) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 px-3 sm:px-4 py-6 sm:py-8 relative overflow-hidden">

                {/* Background Elements */}
                <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute -top-40 -right-40 w-80 h-80 bg-red-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
                    <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-orange-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
                </div>

                {/* Error Card */}
                <div className="w-full max-w-md bg-white/10 backdrop-blur-lg shadow-2xl rounded-2xl sm:rounded-3xl overflow-hidden relative z-10 border border-white/20 p-6 sm:p-8">
                    <div className="text-center">
                        <div className="mx-auto flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-red-500 rounded-full mb-4 sm:mb-6">
                            <FaExclamationTriangle className="text-white text-2xl sm:text-3xl" />
                        </div>

                        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-3 sm:mb-4">
                            Invalid Reset Link
                        </h1>
                        <p className="text-white/90 text-sm sm:text-base mb-6 sm:mb-8 leading-relaxed">
                            This password reset link is invalid or has expired. Please request a new password reset link.
                        </p>

                        <div className="space-y-3 sm:space-y-4">
                            <Link
                                to="/forgot-password"
                                className="w-full inline-flex items-center justify-center space-x-2 bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-200"
                            >
                                <span>Request New Reset Link</span>
                            </Link>

                            <Link
                                to="/login"
                                className="w-full inline-flex items-center justify-center space-x-2 bg-white/20 hover:bg-white/30 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-200"
                            >
                                <FaArrowLeft className="text-sm" />
                                <span>Back to Login</span>
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (success) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 px-3 sm:px-4 py-6 sm:py-8 relative overflow-hidden">

                {/* Background Elements */}
                <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute -top-40 -right-40 w-80 h-80 bg-green-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
                    <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
                </div>

                {/* Success Card */}
                <div className="w-full max-w-md bg-white/10 backdrop-blur-lg shadow-2xl rounded-2xl sm:rounded-3xl overflow-hidden relative z-10 border border-white/20 p-6 sm:p-8">
                    <div className="text-center">
                        <div className="mx-auto flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-green-500 rounded-full mb-4 sm:mb-6">
                            <FaCheckCircle className="text-white text-2xl sm:text-3xl" />
                        </div>

                        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-3 sm:mb-4">
                            Password Reset Successfully!
                        </h1>
                        <p className="text-white/90 text-sm sm:text-base mb-6 sm:mb-8 leading-relaxed">
                            Your password has been successfully reset. You can now sign in with your new password.
                        </p>

                        <button
                            onClick={() => navigate("/login")}
                            className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200"
                        >
                            Sign In Now
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const passwordValidation = validatePassword(formData.password);

    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 px-3 sm:px-4 py-6 sm:py-8 relative overflow-hidden">

            {/* Background Elements */}
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
                <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
            </div>

            {/* Page Heading */}
            <div className="w-full text-center mb-6 sm:mb-8 px-4 relative z-10">
                <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-extrabold text-white mb-3 sm:mb-4">
                    Reset Your
                </h1>
                <div className="inline-flex items-center space-x-2 sm:space-x-3 bg-gradient-to-r from-purple-600 to-blue-600 px-4 sm:px-6 lg:px-8 py-3 sm:py-4 rounded-xl sm:rounded-2xl shadow-2xl">
                    <span className="text-2xl sm:text-3xl">🔐</span>
                    <span className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-white">Password</span>
                </div>
            </div>

            {/* Reset Password Card */}
            <div className="w-full max-w-md bg-white/10 backdrop-blur-lg shadow-2xl rounded-2xl sm:rounded-3xl overflow-hidden relative z-10 border border-white/20">

                {/* Header */}
                <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6 sm:p-8 text-center">
                    <div className="text-4xl sm:text-5xl mb-3 sm:mb-4">🔑</div>
                    <h2 className="text-xl sm:text-2xl font-bold mb-2">Create New Password</h2>
                    <p className="text-white/90 text-sm sm:text-base">
                        Enter your new password below. Make sure it's secure and easy to remember.
                    </p>
                </div>

                {/* Form */}
                <div className="p-6 sm:p-8">
                    <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">

                        {/* New Password Input */}
                        <div className="space-y-2">
                            <label htmlFor="password" className="block text-sm font-medium text-white/90">
                                New Password
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <FaLock className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    id="password"
                                    name="password"
                                    type={showPassword ? "text" : "password"}
                                    required
                                    value={formData.password}
                                    onChange={handleChange}
                                    className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white/95 text-gray-900 placeholder-gray-500 transition-all duration-200"
                                    placeholder="Enter new password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                >
                                    {showPassword ? (
                                        <FaEyeSlash className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                                    ) : (
                                        <FaEye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Password Strength Indicator */}
                        {formData.password && (
                            <div className="space-y-2">
                                <div className="text-xs text-white/70">Password Requirements:</div>
                                <div className="space-y-1">
                                    <div className={`flex items-center space-x-2 text-xs ${passwordValidation.length ? 'text-green-400' : 'text-red-400'}`}>
                                        <div className={`w-2 h-2 rounded-full ${passwordValidation.length ? 'bg-green-400' : 'bg-red-400'}`}></div>
                                        <span>At least 6 characters</span>
                                    </div>
                                    <div className={`flex items-center space-x-2 text-xs ${passwordValidation.upperCase ? 'text-green-400' : 'text-red-400'}`}>
                                        <div className={`w-2 h-2 rounded-full ${passwordValidation.upperCase ? 'bg-green-400' : 'bg-red-400'}`}></div>
                                        <span>One uppercase letter</span>
                                    </div>
                                    <div className={`flex items-center space-x-2 text-xs ${passwordValidation.lowerCase ? 'text-green-400' : 'text-red-400'}`}>
                                        <div className={`w-2 h-2 rounded-full ${passwordValidation.lowerCase ? 'bg-green-400' : 'bg-red-400'}`}></div>
                                        <span>One lowercase letter</span>
                                    </div>
                                    <div className={`flex items-center space-x-2 text-xs ${passwordValidation.numbers ? 'text-green-400' : 'text-red-400'}`}>
                                        <div className={`w-2 h-2 rounded-full ${passwordValidation.numbers ? 'bg-green-400' : 'bg-red-400'}`}></div>
                                        <span>One number</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Confirm Password Input */}
                        <div className="space-y-2">
                            <label htmlFor="confirmPassword" className="block text-sm font-medium text-white/90">
                                Confirm New Password
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <FaLock className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    type={showConfirmPassword ? "text" : "password"}
                                    required
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white/95 text-gray-900 placeholder-gray-500 transition-all duration-200"
                                    placeholder="Confirm new password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                >
                                    {showConfirmPassword ? (
                                        <FaEyeSlash className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                                    ) : (
                                        <FaEye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Password Match Indicator */}
                        {formData.confirmPassword && (
                            <div className={`flex items-center space-x-2 text-xs ${formData.password === formData.confirmPassword ? 'text-green-400' : 'text-red-400'}`}>
                                <div className={`w-2 h-2 rounded-full ${formData.password === formData.confirmPassword ? 'bg-green-400' : 'bg-red-400'}`}></div>
                                <span>{formData.password === formData.confirmPassword ? 'Passwords match' : 'Passwords do not match'}</span>
                            </div>
                        )}

                        {/* Error Message */}
                        {error && (
                            <div className="bg-red-500/20 border border-red-500/50 text-red-200 px-4 py-3 rounded-lg flex items-center space-x-2">
                                <FaExclamationTriangle className="text-red-400 flex-shrink-0" />
                                <span className="text-sm">{error}</span>
                            </div>
                        )}

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={loading || !passwordValidation.isValid || formData.password !== formData.confirmPassword}
                            className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 disabled:from-gray-500 disabled:to-gray-600 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center space-x-2"
                        >
                            {loading ? (
                                <>
                                    <FaSpinner className="animate-spin text-sm" />
                                    <span>Resetting Password...</span>
                                </>
                            ) : (
                                <>
                                    <FaLock className="text-sm" />
                                    <span>Reset Password</span>
                                </>
                            )}
                        </button>
                    </form>

                    {/* Back to Login */}
                    <div className="mt-6 sm:mt-8 text-center">
                        <Link
                            to="/login"
                            className="inline-flex items-center space-x-2 text-white/80 hover:text-white transition-colors duration-200"
                        >
                            <FaArrowLeft className="text-sm" />
                            <span className="text-sm sm:text-base">Back to Login</span>
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ResetPassword;
